import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { UPDATE_DRAFT_POST_REPLY_VARIABLES } from '../../../graphql/graphQLvariables/updatePostReplyDraft.variables';
import { GET_DRAFT_BY_ID } from '../../../graphql/resolvers/draftQueries/getDraftById.gql';
import { GET_DRAFTS_QUERY } from '../../../graphql/resolvers/draftQueries/getDrafts.gql';
import { UPDATE_POST_REPLY_DRAFT_MUTATION } from '../../../graphql/resolvers/replyToPost/mutations/updatePostReplyDraft.gql';
import {
  DocumentStatus,
  PostDraftInterface,
  PostReplyDraftInterfaceState,
  PostReplyDraftsInterfaceState,
} from '../../../interfaces/post.interface';
import type {
  EditPostPayload,
  TextBlock,
} from '../../../interfaces/textEditor.interface';
import { UserState } from '../../../interfaces/user.interface';
import { postReplyDraftSlice } from '../../../store/postReplyDraft.store';
import { postReplyDraftsSlice } from '../../../store/postReplyDrafts.store';
import { userSlice } from '../../../store/user.store';
import { DraftFormRender } from '../../PostDraft/DraftFormRender';
import { handleBlockTypeChange } from '../../TextEditor/handlers/handleBlockTypeChange';

interface EditPostReplyDraftFormRenderProps {
  handleClose: () => void;
  isEditing?: boolean;
}

interface UpdatePostReplyDraftPayload {
  id: string;
  username: string;
  userId: string;
  textBlocks: TextBlock[];
  categories: string[];
  status: DocumentStatus;
  article: boolean;
  parentPostId: string;
  postReplyDraftId: string;
}

/**
 * EditPostReplyDraftFormRenderForm is a React functional component for editing a post reply draft.
 *
 * This component leverages Formik for form state management and handles various
 * actions such as changing the type of content blocks, managing menu anchors,
 * and saving the edited post reply draft.
 */
export const EditPostReplyDraftFormRender: React.FC<
  EditPostReplyDraftFormRenderProps
> = (props: EditPostReplyDraftFormRenderProps) => {
  const params = useParams();
  const { postReplyDraftId, username } = params;
  const dispatch = useDispatch();

  // Post Reply Draft Store Actions
  const {
    GET_POST_REPLY_DRAFT_STORE_ACTION,
    EDITED_DRAFT_STORE_ACTION,
    SAVE_DRAFT_SUCCESS_STORE_ACTION,
    REFRESH_POST_REPLY_DRAFT_STORE_ACTION,
    IS_EDITING_POST_REPLY_DRAFT_STORE_ACTION,
    SAVE_DRAFT_START_STORE_ACTION,
    RESET_POST_REPLY_DRAFT_STATE,
    ADD_POST_REPLY_DRAFT_STORE_ACTION,
    SET_RE_ANALYZE_ACTION,
  } = postReplyDraftSlice.actions;

  const {
    UPDATE_POST_REPLY_DRAFT_STORE_ACTION,
    SET_POST_REPLY_DRAFTS_STORE_ACTION,
    RESET_POST_REPLY_DRAFTS_STORE_ACTION,
  } = postReplyDraftsSlice.actions;

  const { UPDATE_USER, UPDATE_LOGGED_IN_USER } = userSlice.actions;

  // Selectors
  const { postReplyDraft, isEditingPostReplyDraft } = useSelector(
    (state: PostReplyDraftInterfaceState) => state.postReplyDraftStore,
  );
  const { postReplyDrafts } = useSelector(
    (state: PostReplyDraftsInterfaceState) => state.postReplyDraftsStore,
  );
  const { user, loggedInUser } = useSelector(
    (state: UserState) => state.userStore,
  );

  const { handleClose, isEditing } = props;

  // Local State
  const [menuAnchors, setMenuAnchors] = useState<
    Record<string, HTMLElement | null>
  >({});
  const [headingMenuAnchors, setHeadingMenuAnchors] = useState<
    Record<string, HTMLElement | null>
  >({});
  const [updatedDraft, setUpdatedDraft] = useState<PostDraftInterface | null>(
    null,
  );
  const fileInputRef = useRef(null);

  /**
   * Recursively removes __typename from objects (Apollo Client artifact)
   */
  const removeTypename = useCallback((obj: any): any => {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(removeTypename);
    }

    const { __typename, ...rest } = obj;
    return Object.keys(rest).reduce(
      (acc, key) => {
        acc[key] = removeTypename(rest[key]);
        return acc;
      },
      {} as Record<string, any>,
    );
  }, []);

  /**
   * Cleans text blocks by removing __typename and ensuring proper structure
   */
  const cleanBlocks = useCallback(
    (blocks: TextBlock[]): TextBlock[] => {
      if (!blocks) return [];

      return blocks.map((block) => {
        const cleanedBlock = removeTypename(block);

        // Special handling for List type
        if (
          cleanedBlock.type === 'List' &&
          cleanedBlock.data?.items &&
          Array.isArray(cleanedBlock.data.items)
        ) {
          cleanedBlock.data = {
            ...cleanedBlock.data,
            items: cleanedBlock.data.items.map(removeTypename),
          };
        }

        return cleanedBlock;
      });
    },
    [removeTypename],
  );

  // GraphQL Mutation
  const [updatePostReplyDraft, { loading, error }] = useMutation(
    UPDATE_POST_REPLY_DRAFT_MUTATION,
    {
      variables: {
        input: {
          ...UPDATE_DRAFT_POST_REPLY_VARIABLES,
        },
      },
      update(cache, { data: { updatePostReplyDraft: updatedReplyDraft } }) {
        try {
          // Update single draft query cache
          const existingSingleDraft = cache.readQuery<{
            getDraftById?: PostDraftInterface;
          }>({
            query: GET_DRAFT_BY_ID,
            variables: { id: postReplyDraftId },
          });

          if (existingSingleDraft?.getDraftById) {
            cache.writeQuery({
              query: GET_DRAFT_BY_ID,
              variables: { id: postReplyDraftId },
              data: {
                getDraftById: {
                  ...existingSingleDraft.getDraftById,
                  textBlocks: updatedReplyDraft.textBlocks,
                  textBlocksVersions: updatedReplyDraft.textBlocksVersions,
                  updatedAt: updatedReplyDraft.updatedAt,
                  status: updatedReplyDraft.status,
                  categories: updatedReplyDraft.categories,
                  article: updatedReplyDraft.article,
                },
              },
            });
          }

          // Update drafts list query cache
          const existingDrafts = cache.readQuery<{
            getDrafts?: PostDraftInterface[];
          }>({
            query: GET_DRAFTS_QUERY,
          });

          if (existingDrafts?.getDrafts) {
            const updatedDrafts = existingDrafts.getDrafts.map((draft: any) =>
              draft.id === postReplyDraftId
                ? {
                    ...draft,
                    textBlocks: updatedReplyDraft.textBlocks,
                    textBlocksVersions: updatedReplyDraft.textBlocksVersions,
                    updatedAt: updatedReplyDraft.updatedAt,
                    status: updatedReplyDraft.status,
                    categories: updatedReplyDraft.categories,
                    article: updatedReplyDraft.article,
                  }
                : draft,
            );

            cache.writeQuery({
              query: GET_DRAFTS_QUERY,
              data: { getDrafts: updatedDrafts },
            });
          }
        } catch (cacheError) {
          console.error('Error updating cache:', cacheError);
        }
      },
      onCompleted: ({ updatePostReplyDraft: completedDraft }) => {
        setUpdatedDraft(completedDraft);
        dispatch(SAVE_DRAFT_START_STORE_ACTION(true));
      },
      onError: (mutationError) => {
        console.error('updatePostReplyDraft mutation error:', mutationError);
      },
    },
  );

  // Handle successful draft update
  useEffect(() => {
    if (updatedDraft) {
      const payload = {
        postReplyDraftId: postReplyDraftId as string,
        updatedDraft,
      };
      dispatch(RESET_POST_REPLY_DRAFT_STATE(true));
      dispatch(SAVE_DRAFT_START_STORE_ACTION(false));
      dispatch(EDITED_DRAFT_STORE_ACTION(updatedDraft));
      dispatch(SAVE_DRAFT_SUCCESS_STORE_ACTION(true));
      dispatch(GET_POST_REPLY_DRAFT_STORE_ACTION(updatedDraft));
      dispatch(REFRESH_POST_REPLY_DRAFT_STORE_ACTION(true));
      dispatch(UPDATE_POST_REPLY_DRAFT_STORE_ACTION(payload));
    }
  }, [
    updatedDraft,
    postReplyDraftId,
    dispatch,
    RESET_POST_REPLY_DRAFT_STATE,
    SAVE_DRAFT_START_STORE_ACTION,
    EDITED_DRAFT_STORE_ACTION,
    SAVE_DRAFT_SUCCESS_STORE_ACTION,
    GET_POST_REPLY_DRAFT_STORE_ACTION,
    REFRESH_POST_REPLY_DRAFT_STORE_ACTION,
    UPDATE_POST_REPLY_DRAFT_STORE_ACTION,
  ]);

  // Menu Handlers
  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, blockId: string) => {
      setMenuAnchors((prev) => ({ ...prev, [blockId]: event.currentTarget }));
    },
    [],
  );

  const handleMenuClose = useCallback((blockId: string) => {
    setMenuAnchors((prev) => ({ ...prev, [blockId]: null }));
  }, []);

  const handleOpenHeadingMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, blockId: string) => {
      setHeadingMenuAnchors((prev) => ({
        ...prev,
        [blockId]: event.currentTarget,
      }));
    },
    [],
  );

  const handleCloseHeadingMenu = useCallback((blockId: string) => {
    setHeadingMenuAnchors((prev) => ({ ...prev, [blockId]: null }));
  }, []);

  const handleHeadingLevelChange = useCallback(
    (
      index: number,
      level: number,
      setFieldValue: (field: string, value: any) => void,
    ) => {
      setFieldValue(`blocks.${index}.data.level`, level);
    },
    [],
  );

  /**
   * Saves the edited post reply draft
   */
  const handleSaveEditedPostReplyDraft = useCallback(
    (updatedPayload: UpdatePostReplyDraftPayload) => {
      const storePayload = {
        ...postReplyDraft,
        textBlocks: updatedPayload.textBlocks,
        categories: updatedPayload.categories,
        article: updatedPayload.article,
        status: updatedPayload.status,
      };

      const filteredDrafts = postReplyDrafts.filter(
        (draft: PostDraftInterface) => draft.id !== updatedPayload.id,
      );
      const updatedDrafts = [...filteredDrafts, storePayload];

      const updatedUser = {
        ...user,
        postReplyDrafts: updatedDrafts,
      };
      const updatedLoggedInUser = {
        ...loggedInUser,
        postReplyDrafts: updatedDrafts,
      };

      // Optimistic updates to Redux store
      dispatch(ADD_POST_REPLY_DRAFT_STORE_ACTION(storePayload));
      dispatch(SET_POST_REPLY_DRAFTS_STORE_ACTION(updatedDrafts));
      dispatch(UPDATE_USER(updatedUser));
      dispatch(UPDATE_LOGGED_IN_USER(updatedLoggedInUser));

      // Execute mutation
      updatePostReplyDraft({ variables: { input: updatedPayload } })
        .then(() => {
          dispatch(IS_EDITING_POST_REPLY_DRAFT_STORE_ACTION(false));
          handleClose();
        })
        .catch((e) => {
          console.error('Failed to update post reply draft:', e);
        });
    },
    [
      postReplyDraft,
      postReplyDrafts,
      user,
      loggedInUser,
      dispatch,
      updatePostReplyDraft,
      handleClose,
      ADD_POST_REPLY_DRAFT_STORE_ACTION,
      SET_POST_REPLY_DRAFTS_STORE_ACTION,
      UPDATE_USER,
      UPDATE_LOGGED_IN_USER,
      IS_EDITING_POST_REPLY_DRAFT_STORE_ACTION,
    ],
  );

  /**
   * Prepares payload and triggers save
   */
  const handlePreSaveEditedPostReplyDraft = useCallback(
    (payload: EditPostPayload) => {
      const blocks = cleanBlocks(
        payload.textBlocks ?? payload.blocks,
      ) as TextBlock[];

      const updatedPayload: UpdatePostReplyDraftPayload = {
        id: postReplyDraftId as string,
        postReplyDraftId: postReplyDraftId as string,
        username: username as string,
        userId: user?.id ?? loggedInUser?.id ?? '',
        textBlocks: blocks,
        categories: postReplyDraft?.categories ?? [],
        status: postReplyDraft?.status as DocumentStatus,
        article: postReplyDraft?.article ?? false,
        parentPostId: postReplyDraft?.parentPostId as string,
      };

      dispatch(SET_RE_ANALYZE_ACTION(true));
      dispatch(RESET_POST_REPLY_DRAFT_STATE(true));
      dispatch(RESET_POST_REPLY_DRAFTS_STORE_ACTION(true));

      handleSaveEditedPostReplyDraft(updatedPayload);
    },
    [
      postReplyDraftId,
      username,
      postReplyDraft,
      loggedInUser,
      cleanBlocks,
      dispatch,
      handleSaveEditedPostReplyDraft,
      SET_RE_ANALYZE_ACTION,
      RESET_POST_REPLY_DRAFT_STATE,
      RESET_POST_REPLY_DRAFTS_STORE_ACTION,
    ],
  );

  // Prepare initial form values
  const initialValues = {
    blocks: cleanBlocks(
      (postReplyDraft?.textBlocks as TextBlock[]) ??
        postReplyDraft?.blocks ??
        [],
    ).map((block) => ({
      ...block,
      data: {
        ...block.data,
        paragraph: block.data?.paragraph || '',
        header: block.data?.header || '',
        level: block.data?.level || 1,
        items: block.data?.items || [],
        code: block.data?.code || '',
        caption: block.data?.caption || '',
        style: block.data?.style || 'unordered',
      },
    })),
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handlePreSaveEditedPostReplyDraft as any}
      enableReinitialize
    >
      {({
        handleSubmit,
        values,
        setFieldValue,
        isSubmitting,
        errors,
        touched,
      }) => (
        <DraftFormRender
          blockTypes={[
            'Paragraph',
            'Heading',
            'List',
            'Image',
            'Code',
            'Quote',
          ]}
          errors={errors as any}
          fileInputRef={fileInputRef}
          handleBlockTypeChange={handleBlockTypeChange as any}
          handleCloseHeadingMenu={handleCloseHeadingMenu}
          handleHeadingLevelChange={handleHeadingLevelChange}
          handleMenuClick={handleMenuClick as any}
          handleMenuClose={handleMenuClose}
          handleOpenHeadingMenu={handleOpenHeadingMenu}
          handleSave={handlePreSaveEditedPostReplyDraft}
          handleSubmit={handleSubmit}
          headingMenuAnchors={headingMenuAnchors}
          isSubmitting={isSubmitting || loading}
          menuAnchors={menuAnchors}
          payload={{
            username: username as string,
            userId: user?.id ?? loggedInUser?.id ?? '',
            textBlocks: values.blocks,
            draftId: postReplyDraftId as string,
            postReplyDraftId: postReplyDraftId as string,
            status: postReplyDraft?.status,
            categories: postReplyDraft?.categories ?? [],
            article: postReplyDraft?.article ?? false,
            parentPostId: postReplyDraft?.parentPostId as string,
          }}
          setFieldValue={setFieldValue}
          touched={touched}
          values={values as any}
          isEditing={isEditingPostReplyDraft as boolean}
          className="EDIT__POST__REPLY__DRAFT__FORM__RENDER"
        />
      )}
    </Formik>
  );
};
